// ROTEIRO DE VENDAS e OBJEÇÕES assistidos por IA: /v1/sales-script/*. É o
// material que o VENDEDOR HUMANO usa — os tópicos que ele segue na conversa e
// os pares "o que o lead diz" -> "como rebater". A IA aqui só faz o trabalho
// chato: ler um texto colado (ou um .docx/.pdf) e devolvê-lo objetivo.
//
// NÃO confundir com o roteiro da IA de VOZ (./voice.js): aquele é um documento
// versionado no LAI Compass que o voice-talk EXECUTA na ligação; este é texto
// que uma pessoa lê. Os dois se chamam "roteiro" e não têm rota, tipo nem tela
// em comum — por isso são módulos separados, e não um só chamado "roteiros".
//
// O resultado NÃO é persistido por estas rotas: as telas gravam os tópicos e as
// objeções no metadata da organização, pelo Better Auth (lib/sales-script.ts e
// lib/objections.ts). Daí este módulo não ter arquivo de hooks: não existe uma
// query key deste domínio para centralizar — as duas telas só fazem POST e
// mostram o retorno na hora.
//
// Objeções moram aqui, e não em módulo próprio, porque são a MESMA feature do
// back: mesma entrada (o material de vendas costuma trazer as duas coisas),
// mesmo /extract, mesmo teto de 20.000 caracteres. Separá-las deixaria um
// arquivo com uma função que importaria as duas constantes do outro.
import { api, apiUpload, toApiError } from "./client.js";

// Script de vendas: texto cru (colado do Word/PDF) -> tópicos objetivos, na ordem
// da conversa. Quem fala com a LLM é o back (a chave vive lá, nunca no browser).
// O teto de 20.000 caracteres espelha o schema do back — acima disso ele dá 422.
export const MAX_SALES_SCRIPT_CHARS = 20_000;

export async function improveSalesScript(content: string): Promise<string[]> {
  const { data, error } = await api<{ topics: string[] }>("/v1/sales-script/improve", {
    method: "POST",
    body: { content },
  });
  if (error) throw toApiError("POST /v1/sales-script/improve", error);
  return data.topics;
}

// Extração do texto de um roteiro (.docx, .doc ou .pdf). Quem lê o arquivo é o
// back: cada formato tem seu parser (zip de XML, binário OLE antigo, árvore de
// objetos do PDF) e nenhum é confiável no browser. O arquivo não é guardado em
// lugar nenhum — entra, vira texto, volta.
export const MAX_SALES_SCRIPT_FILE_BYTES = 5 * 1024 * 1024; // espelha o maxSize do back

export async function extractSalesScriptFile(file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  const { content } = await apiUpload<{ content: string }>("/v1/sales-script/extract", form);
  return content;
}

// Objeções: mesmo texto cru de entrada (o material de vendas costuma trazer as
// duas coisas), mas a saída são pares "o que o lead diz" -> "como rebater". Vive
// na mesma feature do back, por isso reaproveita o /extract e o teto de 20.000.
export interface ObjectionSuggestion {
  objection: string;
  response: string;
}

export async function improveObjections(content: string): Promise<ObjectionSuggestion[]> {
  const { data, error } = await api<{ objections: ObjectionSuggestion[] }>(
    "/v1/sales-script/objections",
    { method: "POST", body: { content } },
  );
  if (error) throw toApiError("POST /v1/sales-script/objections", error);
  return data.objections;
}
